import * as React from "react";
import { cn } from "../utils/cn";
import { Progress, type ProgressColor } from "../primitives/progress";
import { OrgAvatar } from "./OrgAvatar";
import { Heading, Text, Mono } from "../primitives/typography";
import { Users } from "lucide-react";

export interface SeatUsageOrg {
    id: string;
    name: string;
    subtitle?: string;
    fallback: string;
    used: number;
    total: number;
}

export interface SeatUsageCardProps extends React.HTMLAttributes<HTMLDivElement> {
    orgs: SeatUsageOrg[];
    onAllOrgsClick?: () => void;
}

function getBarColor(pct: number): ProgressColor {
    if (pct > 100) return "destructive";
    if (pct >= 90) return "warning";
    return "default";
}

const SeatUsageCard = React.forwardRef<HTMLDivElement, SeatUsageCardProps>(
    ({ className, orgs, onAllOrgsClick, ...props }, ref) => (
        <div ref={ref} className={cn("border-border bg-card flex flex-col rounded-2xl border", className)} {...props}>
            {/* Header */}
            <div className="flex items-start justify-between px-5 pt-5 pb-4">
                <div>
                    <div className="flex items-center gap-2">
                        <Users className="text-warning h-4 w-4 shrink-0" />
                        <Heading level={6} className="font-semibold">
                            Seat usage
                        </Heading>
                    </div>
                    <Text size="sm" muted>
                        Top 5 orgs by capacity
                    </Text>
                </div>
                <button
                    onClick={onAllOrgsClick}
                    className="text-secondary hover:text-secondary/80 text-sm font-medium whitespace-nowrap transition-colors"
                >
                    All organizations →
                </button>
            </div>

            {/* Org rows */}
            <div className="divide-border flex flex-col divide-y">
                {orgs.map((org) => {
                    const pct = Math.round((org.used / org.total) * 100);
                    const color = getBarColor(pct);
                    const colorClass = {
                        default: "text-secondary",
                        warning: "text-warning",
                        destructive: "text-destructive",
                        success: "text-success",
                    }[color];

                    return (
                        <div key={org.id} className="flex items-center gap-3 px-5 py-4">
                            <OrgAvatar id={org.id} fallback={org.fallback} size="sm" />

                            <div className="min-w-0 flex-1">
                                <Text size="sm" className="font-display font-semibold">
                                    {org.name}
                                </Text>
                                {org.subtitle && (
                                    <Text size="xs" className="text-muted-foreground">
                                        {org.subtitle}
                                    </Text>
                                )}
                            </div>

                            <div className="flex w-48 shrink-0 flex-col gap-1 sm:w-56">
                                <Progress value={Math.min(100, pct)} color={color} className="h-1.5" />
                                <div className="flex justify-end gap-1 text-xs">
                                    <Mono size="xs" className="text-muted-foreground">
                                        {org.used.toLocaleString()} / {org.total.toLocaleString()}
                                    </Mono>
                                </div>
                                <div className={cn("text-right text-xs font-semibold", colorClass)}>
                                    {pct}%{pct > 100 ? " · over" : pct >= 90 ? "" : ""}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    ),
);
SeatUsageCard.displayName = "SeatUsageCard";

export { SeatUsageCard, getBarColor };
